import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/hooks/useAuth";
import { useTranslation } from "react-i18next";
import { getCollectionsByOrganizer, getParticipatingCollections, deleteCollection } from "@/services/collections";
import { getMessageCountsForCollections } from "@/services/messages";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Plus, CircleDollarSign, SlidersHorizontal, X, Check, Trash2, Loader2 } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import AppHeader from "@/components/AppHeader";
import type { Tables } from "@/integrations/supabase/types";
import { useCurrency } from "@/hooks/useCurrency";
import { useIsMobile } from "@/hooks/use-mobile";
import { useToast } from "@/hooks/use-toast";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const OCCASION_OPTIONS: { value: string; key: string; emoji: string }[] = [
  { value: "birthday", key: "birthday", emoji: "🎂" },
  { value: "farewell", key: "farewell", emoji: "👋" },
  { value: "baby", key: "newBaby", emoji: "👶" },
  { value: "wedding", key: "wedding", emoji: "💍" },
  { value: "thank-you", key: "thankYou", emoji: "🙏" },
  { value: "retirement", key: "retirement", emoji: "🎉" },
  { value: "get-well", key: "getWell", emoji: "💐" },
  { value: "sympathy", key: "sympathy", emoji: "🕊️" },
  { value: "graduation", key: "graduation", emoji: "🎓" },
  { value: "teacher", key: "teacher", emoji: "📚" },
  { value: "anniversary", key: "anniversary", emoji: "❤️" },
  { value: "christmas", key: "christmas", emoji: "🎄" },
  { value: "valentines", key: "valentines", emoji: "💝" },
  { value: "congratulations", key: "congratulations", emoji: "🏆" },
];

const STATUS_FILTER_OPTIONS: { value: "active" | "draft" | "closed"; key: string }[] = [
  { value: "active", key: "active" },
  { value: "draft", key: "draft" },
  { value: "closed", key: "closed" },
];

const TYPE_FILTER_OPTIONS: { value: "card_only" | "card_collection"; key: string; emoji: string }[] = [
  { value: "card_only", key: "card_only", emoji: "💌" },
  { value: "card_collection", key: "card_collection", emoji: "🎁" },
];

type FilterCategory = "status" | "occasion" | "type";

const cardTypes = [
  { emoji: "🎂", label: "Birthday" },
  { emoji: "💍", label: "Wedding" },
  { emoji: "👶", label: "New Baby" },
  { emoji: "🎓", label: "Graduation" },
  { emoji: "👋", label: "Farewell" },
  { emoji: "🙏", label: "Thank You" },
  { emoji: "🎉", label: "Retirement" },
  { emoji: "❤️", label: "Anniversary" },
  { emoji: "🎄", label: "Christmas" },
  { emoji: "💝", label: "Valentine's" },
  { emoji: "🏆", label: "Congrats" },
];

const FlippingCardTypes = () => {
  const [index, setIndex] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setIndex((prev) => (prev + 1) % cardTypes.length);
    }, 2000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="flex justify-center mb-6" style={{ perspective: "600px" }}>
      <div className="relative w-28 h-36">
        <AnimatePresence mode="wait">
          <motion.div
            key={index}
            initial={{ rotateY: 90, opacity: 0 }}
            animate={{ rotateY: 0, opacity: 1 }}
            exit={{ rotateY: -90, opacity: 0 }}
            transition={{ duration: 0.4, ease: "easeInOut" }}
            className="absolute inset-0 rounded-2xl bg-card border border-border/60 shadow-md flex flex-col items-center justify-center gap-2"
            style={{ backfaceVisibility: "hidden" }}
          >
            <span className="text-4xl">{cardTypes[index].emoji}</span>
            <span className="text-xs font-medium text-muted-foreground font-sans">{cardTypes[index].label}</span>
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
};

// Narrow shape covering only the columns the dashboard grid reads.
// Mirrors the slim select in services/collections.ts so the query
// result type matches this without needing the full Row.
type DashboardCollection = Pick<
  Tables<"collections">,
  | "id"
  | "organizer_id"
  | "title"
  | "recipient_name"
  | "share_slug"
  | "status"
  | "product_image"
  | "product_name"
  | "total_collected"
  | "type"
  | "pool_type"
  | "occasion"
  | "created_at"
> & {
  message_count?: number;
  template_image_url?: string | null;
};

type TabKey = "my_cards" | "participating";

const Dashboard = () => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<TabKey>("my_cards");
  const currency = useCurrency();
  const [statusFilters, setStatusFilters] = useState<string[]>([]);
  const [occasionFilters, setOccasionFilters] = useState<string[]>([]);
  const [typeFilters, setTypeFilters] = useState<string[]>([]);
  const [filterOpen, setFilterOpen] = useState(false);
  const [activeCategory, setActiveCategory] = useState<FilterCategory>("status");
  const isMobile = useIsMobile();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Draft delete state — separate from the main tile click handler so
  // clicking the trash icon doesn't also navigate into the draft's
  // resume flow. The AlertDialog is rendered once at the page level
  // and parameterised by the draft we're about to delete.
  const [draftToDelete, setDraftToDelete] = useState<DashboardCollection | null>(null);
  const [deletingDraft, setDeletingDraft] = useState(false);

  const confirmDeleteDraft = async () => {
    if (!draftToDelete) return;
    setDeletingDraft(true);
    const { error } = await deleteCollection(draftToDelete.id);
    setDeletingDraft(false);
    if (error) {
      toast({
        variant: "destructive",
        title: t("common.error", { defaultValue: "Something went wrong" }),
        description: error.message,
      });
      return;
    }
    // Invalidate the dashboard query so the deleted draft disappears
    // without a full refetch dance — react-query refetches in the
    // background and drops the deleted row.
    queryClient.invalidateQueries({ queryKey: ["dashboard", "collections"] });
    toast({
      title: t("dashboard.draftDeleted", { defaultValue: "Draft deleted" }),
    });
    setDraftToDelete(null);
  };

  const toggleStatus = (value: string) =>
    setStatusFilters((prev) =>
      prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]
    );
  const toggleOccasion = (value: string) =>
    setOccasionFilters((prev) =>
      prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]
    );
  const toggleType = (value: string) =>
    setTypeFilters((prev) =>
      prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]
    );

  useEffect(() => {
    if (!loading && !user) navigate("/auth");
  }, [user, loading, navigate]);

  // Dashboard cards fetch — migrated to react-query so switching tabs
  // or bouncing back from a card detail page uses the in-memory cache
  // instead of re-issuing the 2+ Supabase round trips every time.
  // Cache TTL is governed by the global defaults in App.tsx (30s
  // stale, 5min gc), which means the list stays instant for the
  // typical "go look at a card, come back" navigation pattern.
  const {
    data: dashboardData,
    isLoading: loadingData,
    isError: fetchError,
  } = useQuery({
    queryKey: ["dashboard", "collections", user?.id, user?.email],
    enabled: !!user,
    queryFn: async () => {
      const [ownResult, participatingResult] = await Promise.all([
        getCollectionsByOrganizer(user!.id),
        getParticipatingCollections(user!.email || ""),
      ]);
      if (ownResult.error) throw ownResult.error;

      const ownRows = ownResult.data || [];
      const ownIds = ownRows.map((c) => c.id);
      // My Cards tiles don't render the signer count, so we skip the
      // message count fetch for owned collections entirely — it was a
      // full extra round trip blocking the list render.
      const myCards: DashboardCollection[] = ownRows.map(({ card_templates, ...rest }) => ({
        ...rest,
        template_image_url: card_templates?.image_url ?? null,
      }));

      const ownIdSet = new Set(ownIds);
      const participatingRows = (participatingResult.data || []).filter((c) => !ownIdSet.has(c.id));
      const partIds = participatingRows.map((c) => c.id);
      const partCounts = partIds.length > 0 ? await getMessageCountsForCollections(partIds) : new Map();
      const participating: DashboardCollection[] = participatingRows.map(({ card_templates, ...rest }) => ({
        ...rest,
        message_count: partCounts.get(rest.id) || 0,
        template_image_url: card_templates?.image_url ?? null,
      }));

      return { myCards, participating };
    },
  });

  const myCards = dashboardData?.myCards ?? [];
  const participating = dashboardData?.participating ?? [];

  const sourceCollections = activeTab === "my_cards" ? myCards : participating;
  const isParticipating = activeTab === "participating";

  const collections = sourceCollections.filter((c) => {
    if (statusFilters.length > 0 && !statusFilters.includes(c.status || "")) return false;
    if (occasionFilters.length > 0 && !occasionFilters.includes(c.occasion || "")) return false;
    if (typeFilters.length > 0 && !typeFilters.includes(c.type || "")) return false;
    return true;
  });

  const hasActiveFilters =
    statusFilters.length > 0 || occasionFilters.length > 0 || typeFilters.length > 0;
  const activeFilterCount =
    statusFilters.length + occasionFilters.length + typeFilters.length;
  const clearFilters = () => {
    setStatusFilters([]);
    setOccasionFilters([]);
    setTypeFilters([]);
  };

  const filterPanel = (
    <div className="flex flex-col">
      {/* Selected chips */}
      {hasActiveFilters && (
        <div className="flex flex-wrap gap-2 pb-4 mb-4 border-b border-border">
          {statusFilters.map((s) => (
            <button
              key={`chip-status-${s}`}
              onClick={() => toggleStatus(s)}
              className="h-7 ps-3 pe-2 rounded-full bg-primary/10 text-primary text-xs font-medium flex items-center gap-1.5 hover:bg-primary/15 transition-colors"
            >
              <span>{t(`dashboard.status.${s}`)}</span>
              <X size={12} />
            </button>
          ))}
          {occasionFilters.map((o) => {
            const opt = OCCASION_OPTIONS.find((x) => x.value === o);
            if (!opt) return null;
            return (
              <button
                key={`chip-occ-${o}`}
                onClick={() => toggleOccasion(o)}
                className="h-7 ps-2.5 pe-2 rounded-full bg-primary/10 text-primary text-xs font-medium flex items-center gap-1.5 hover:bg-primary/15 transition-colors"
              >
                <span className="text-sm leading-none">{opt.emoji}</span>
                <span>{t(`occasions.${opt.key}`)}</span>
                <X size={12} />
              </button>
            );
          })}
          {typeFilters.map((tp) => {
            const opt = TYPE_FILTER_OPTIONS.find((x) => x.value === tp);
            if (!opt) return null;
            return (
              <button
                key={`chip-type-${tp}`}
                onClick={() => toggleType(tp)}
                className="h-7 ps-2.5 pe-2 rounded-full bg-primary/10 text-primary text-xs font-medium flex items-center gap-1.5 hover:bg-primary/15 transition-colors"
              >
                <span className="text-sm leading-none">{opt.emoji}</span>
                <span>{t(`dashboard.type.${opt.key}`)}</span>
                <X size={12} />
              </button>
            );
          })}
        </div>
      )}

      {/* Two-column body */}
      <div className="flex border border-border rounded-2xl overflow-hidden h-[50vh] sm:h-[55vh] max-h-[420px]">
        {/* Categories (left) */}
        <div className="w-32 sm:w-40 shrink-0 bg-muted/30 border-e border-border overflow-y-auto">
          <button
            onClick={() => setActiveCategory("status")}
            className={`w-full text-start px-4 py-3.5 text-sm font-medium transition-colors border-s-2 ${
              activeCategory === "status"
                ? "bg-background text-foreground border-primary"
                : "text-muted-foreground hover:text-foreground border-transparent"
            }`}
          >
            <span>{t("dashboard.filter.status", { defaultValue: "Status" })}</span>
            {statusFilters.length > 0 && (
              <span className="ms-1 text-primary">({statusFilters.length})</span>
            )}
          </button>
          <button
            onClick={() => setActiveCategory("occasion")}
            className={`w-full text-start px-4 py-3.5 text-sm font-medium transition-colors border-s-2 ${
              activeCategory === "occasion"
                ? "bg-background text-foreground border-primary"
                : "text-muted-foreground hover:text-foreground border-transparent"
            }`}
          >
            <span>{t("dashboard.filter.occasion", { defaultValue: "Occasion" })}</span>
            {occasionFilters.length > 0 && (
              <span className="ms-1 text-primary">({occasionFilters.length})</span>
            )}
          </button>
          <button
            onClick={() => setActiveCategory("type")}
            className={`w-full text-start px-4 py-3.5 text-sm font-medium transition-colors border-s-2 ${
              activeCategory === "type"
                ? "bg-background text-foreground border-primary"
                : "text-muted-foreground hover:text-foreground border-transparent"
            }`}
          >
            <span>{t("dashboard.filter.type", { defaultValue: "Type" })}</span>
            {typeFilters.length > 0 && (
              <span className="ms-1 text-primary">({typeFilters.length})</span>
            )}
          </button>
        </div>

        {/* Options (right) */}
        <div className="flex-1 overflow-y-auto p-2">
          {activeCategory === "status" ? (
            <div className="space-y-1">
              {STATUS_FILTER_OPTIONS.map((opt) => {
                const checked = statusFilters.includes(opt.value);
                return (
                  <button
                    key={opt.value}
                    onClick={() => toggleStatus(opt.value)}
                    className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium text-start transition-colors ${
                      checked ? "bg-primary/10 text-primary" : "hover:bg-muted text-foreground"
                    }`}
                  >
                    <span
                      className={`w-5 h-5 rounded-md border-2 flex items-center justify-center shrink-0 transition-colors ${
                        checked
                          ? "bg-primary border-primary text-primary-foreground"
                          : "border-border"
                      }`}
                    >
                      {checked && <Check size={12} strokeWidth={3} />}
                    </span>
                    <span className="flex-1 truncate">{t(`dashboard.status.${opt.key}`)}</span>
                  </button>
                );
              })}
            </div>
          ) : activeCategory === "occasion" ? (
            <div className="space-y-1">
              {OCCASION_OPTIONS.map((opt) => {
                const checked = occasionFilters.includes(opt.value);
                return (
                  <button
                    key={opt.value}
                    onClick={() => toggleOccasion(opt.value)}
                    className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium text-start transition-colors ${
                      checked ? "bg-primary/10 text-primary" : "hover:bg-muted text-foreground"
                    }`}
                  >
                    <span
                      className={`w-5 h-5 rounded-md border-2 flex items-center justify-center shrink-0 transition-colors ${
                        checked
                          ? "bg-primary border-primary text-primary-foreground"
                          : "border-border"
                      }`}
                    >
                      {checked && <Check size={12} strokeWidth={3} />}
                    </span>
                    <span className="text-base leading-none">{opt.emoji}</span>
                    <span className="flex-1 truncate">{t(`occasions.${opt.key}`)}</span>
                  </button>
                );
              })}
            </div>
          ) : (
            <div className="space-y-1">
              {TYPE_FILTER_OPTIONS.map((opt) => {
                const checked = typeFilters.includes(opt.value);
                return (
                  <button
                    key={opt.value}
                    onClick={() => toggleType(opt.value)}
                    className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium text-start transition-colors ${
                      checked ? "bg-primary/10 text-primary" : "hover:bg-muted text-foreground"
                    }`}
                  >
                    <span
                      className={`w-5 h-5 rounded-md border-2 flex items-center justify-center shrink-0 transition-colors ${
                        checked
                          ? "bg-primary border-primary text-primary-foreground"
                          : "border-border"
                      }`}
                    >
                      {checked && <Check size={12} strokeWidth={3} />}
                    </span>
                    <span className="text-base leading-none">{opt.emoji}</span>
                    <span className="flex-1 truncate">{t(`dashboard.type.${opt.key}`)}</span>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Footer */}
      <div className="flex items-center gap-3 mt-5">
        <Button
          variant="outline"
          onClick={clearFilters}
          disabled={!hasActiveFilters}
          className="rounded-full flex-1"
        >
          {t("dashboard.filter.clearFilter", { defaultValue: "Clear filter" })}
        </Button>
        <Button
          onClick={() => setFilterOpen(false)}
          className="rounded-full flex-1"
        >
          {t("dashboard.filter.apply", { defaultValue: "Apply" })}
        </Button>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-pulse text-muted-foreground font-sans">{t("dashboard.loading")}</div>
      </div>
    );
  }

  const renderNewCardButton = () => (
    <button
      key="new-card-button"
      type="button"
      onClick={() => navigate("/create")}
      aria-label={t("dashboard.newCard", { defaultValue: "New Card" })}
      className="bg-primary/5 rounded-2xl border-2 border-dashed border-primary/40 hover:border-primary hover:bg-primary/10 transition-all duration-300 group flex items-center justify-center"
    >
      <div className="w-16 h-16 rounded-full bg-primary text-primary-foreground flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
        <Plus size={28} strokeWidth={2.5} />
      </div>
    </button>
  );

  return (
    <div className="min-h-screen bg-background">
      {/* On desktop the in-grid "New card" tile is the primary create
          affordance, so we hide the redundant header + button there.
          Mobile still shows it because the in-grid tile is below the
          fold. */}
      <AppHeader
        userMenuVariant="dashboard"
        actions={
          <button
            onClick={() => navigate("/create")}
            className="sm:hidden w-9 h-9 rounded-full border border-border flex items-center justify-center hover:bg-muted transition-colors text-muted-foreground hover:text-foreground"
          >
            <Plus size={18} />
          </button>
        }
      />

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        {/* Tabs + Filter button */}
        <div className="flex items-center gap-2 mb-8">
          <div className="flex gap-1 bg-muted/50 rounded-full p-1 w-fit">
            <button
              onClick={() => setActiveTab("my_cards")}
              className={`px-5 py-2 rounded-full text-sm font-medium transition-all ${
                activeTab === "my_cards"
                  ? "bg-background text-foreground shadow-sm"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              {t("dashboard.myCards", { defaultValue: "My Cards" })}
            </button>
            <button
              onClick={() => setActiveTab("participating")}
              className={`px-5 py-2 rounded-full text-sm font-medium transition-all ${
                activeTab === "participating"
                  ? "bg-background text-foreground shadow-sm"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              {t("dashboard.participating", { defaultValue: "Participating" })}
              {participating.length > 0 && (
                <span className="ms-1.5 text-[10px] bg-primary/10 text-primary px-1.5 py-0.5 rounded-full">
                  {participating.length}
                </span>
              )}
            </button>
          </div>

          {/* Single Filter button */}
          <button
            onClick={() => setFilterOpen(true)}
            aria-label={t("dashboard.filter.button", { defaultValue: "Filter" })}
            className={`relative ms-auto h-10 px-4 rounded-full border text-sm font-medium flex items-center gap-2 transition-colors ${
              hasActiveFilters
                ? "border-primary bg-primary/10 text-primary"
                : "border-border text-muted-foreground hover:text-foreground hover:border-foreground/30"
            }`}
          >
            <SlidersHorizontal size={15} />
            <span className="hidden sm:inline">
              {t("dashboard.filter.button", { defaultValue: "Filter" })}
            </span>
            {hasActiveFilters && (
              <span className="min-w-5 h-5 px-1.5 rounded-full bg-primary text-primary-foreground text-[10px] font-bold flex items-center justify-center">
                {activeFilterCount}
              </span>
            )}
          </button>
        </div>

        {/* Filter modal: Sheet on mobile, Dialog on desktop */}
        {isMobile ? (
          <Sheet open={filterOpen} onOpenChange={setFilterOpen}>
            <SheetContent
              side="bottom"
              className="rounded-t-3xl max-h-[85vh] overflow-y-auto p-6"
            >
              <SheetHeader className="mb-4">
                <SheetTitle className="text-start">
                  {t("dashboard.filter.title", { defaultValue: "Filters" })}
                </SheetTitle>
              </SheetHeader>
              {filterPanel}
            </SheetContent>
          </Sheet>
        ) : (
          <Dialog open={filterOpen} onOpenChange={setFilterOpen}>
            <DialogContent className="sm:max-w-xl max-h-[90vh] overflow-y-auto rounded-2xl">
              <DialogHeader>
                <DialogTitle>
                  {t("dashboard.filter.title", { defaultValue: "Filters" })}
                </DialogTitle>
              </DialogHeader>
              {filterPanel}
            </DialogContent>
          </Dialog>
        )}

        {fetchError ? (
          <div className="text-center py-20">
            <h2 className="text-2xl font-bold text-foreground mb-2">{t("dashboard.somethingWrong")}</h2>
            <p className="text-muted-foreground mb-6">{t("dashboard.couldntLoad")}</p>
            <Button onClick={() => window.location.reload()} className="rounded-full" size="lg">
              {t("dashboard.retry")}
            </Button>
          </div>
        ) : loadingData ? (
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3 sm:gap-4 lg:gap-5">
            {Array.from({ length: 12 }).map((_, i) => (
              <div
                key={i}
                className="bg-card rounded-2xl overflow-hidden border border-border/60"
              >
                <div className="aspect-[3/4] bg-muted animate-pulse" />
                <div className="px-3.5 py-3 space-y-1.5">
                  <div className="h-3.5 bg-muted rounded w-3/4 animate-pulse" />
                  <div className="h-2.5 bg-muted rounded w-1/2 animate-pulse" />
                </div>
              </div>
            ))}
          </div>
        ) : collections.length === 0 ? (
          hasActiveFilters && sourceCollections.length > 0 ? (
            <motion.div
              key={`${activeTab}-filtered-empty`}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-center py-16"
            >
              <h2 className="text-2xl font-bold text-foreground mb-2">
                {t("dashboard.filter.noMatches", { defaultValue: "No matching cards" })}
              </h2>
              <p className="text-muted-foreground font-sans mb-6">
                {t("dashboard.filter.noMatchesDesc", {
                  defaultValue: "Try changing or clearing your filters.",
                })}
              </p>
              <Button
                onClick={clearFilters}
                variant="outline"
                className="rounded-full gap-2"
                size="lg"
              >
                <X size={16} /> {t("dashboard.filter.clear", { defaultValue: "Clear filters" })}
              </Button>
            </motion.div>
          ) : (
            <motion.div
              key={activeTab}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-center py-16"
            >
              {!isParticipating && <FlippingCardTypes />}
              <h2 className="text-2xl font-bold text-foreground mb-2">
                {isParticipating
                  ? t("dashboard.noParticipating", { defaultValue: "No cards yet" })
                  : t("dashboard.noCardsYet")}
              </h2>
              <p className="text-muted-foreground font-sans mb-6">
                {isParticipating
                  ? t("dashboard.noParticipatingDesc", { defaultValue: "Cards you RSVP to, sign, or chip in to will appear here." })
                  : t("dashboard.noCardsDescription")}
              </p>
              {!isParticipating && (
                <Button onClick={() => navigate("/create")} className="rounded-full gap-2" size="lg">
                  <Plus size={18} /> {t("dashboard.createFirst")}
                </Button>
              )}
            </motion.div>
          )
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3 sm:gap-4 lg:gap-5">
            {/* New card button - only on My Cards tab, shown first */}
            {!isParticipating && renderNewCardButton()}

            {collections.map((c) => {
              const collected = Number(c.total_collected || 0);
              const signerCount = c.message_count || 0;
              // Draft rows deep-link back into the Create Card wizard
              // so the user can resume editing instead of landing on
              // the card-manage screen where most fields are missing.
              const isDraft = c.status === "draft";

              return (
                <div
                  key={c.id}
                  className="bg-card rounded-2xl overflow-hidden hover:shadow-lg transition-all duration-300 group cursor-pointer border border-border/60 hover:border-border"
                  onClick={() => {
                    if (isParticipating) {
                      navigate(`/card/${c.share_slug}`);
                    } else if (isDraft) {
                      navigate(`/create?draft=${c.id}`);
                    } else {
                      navigate(`/card/${c.share_slug}/manage`, { state: { fromDashboard: true } });
                    }
                  }}
                >
                  <div className="relative aspect-[3/4] p-1 overflow-hidden">
                    {c.product_image || c.template_image_url ? (
                      <>
                        {c.product_image ? (
                          <img
                            src={c.product_image}
                            alt={c.product_name || c.title || c.recipient_name || "Product"}
                            loading="lazy"
                            decoding="async"
                            className={`absolute inset-1 object-cover rounded-md transition-opacity duration-300 ${
                              c.template_image_url ? "group-hover:opacity-0" : ""
                            }`}
                          />
                        ) : null}
                        {c.template_image_url ? (
                          <img
                            src={c.template_image_url}
                            alt={c.title || c.recipient_name || "Card"}
                            loading="lazy"
                            decoding="async"
                            className={`absolute inset-1 object-cover rounded-md transition-opacity duration-300 ${
                              c.product_image ? "opacity-0 group-hover:opacity-100" : ""
                            }`}
                          />
                        ) : null}
                      </>
                    ) : (
                      <div className="w-full h-full flex items-center justify-center bg-secondary rounded-md">
                        <span className="text-4xl font-serif font-extralight text-muted-foreground/40">{c.recipient_name?.[0]?.toUpperCase() || "?"}</span>
                      </div>
                    )}
                    {/* Delete button for drafts — top-right, appears
                        on tile hover on desktop and is always visible
                        on mobile (where hover doesn't exist). Stops
                        the tile click from firing so the draft opens
                        a confirm dialog instead of jumping to the
                        wizard. */}
                    {isDraft && (
                      <button
                        type="button"
                        aria-label={t("dashboard.deleteDraft", { defaultValue: "Delete draft" })}
                        onClick={(e) => {
                          e.stopPropagation();
                          setDraftToDelete(c);
                        }}
                        className="absolute top-2.5 end-2.5 h-7 w-7 rounded-full bg-background/95 backdrop-blur-sm flex items-center justify-center text-muted-foreground shadow-sm transition-all sm:opacity-0 sm:group-hover:opacity-100 hover:text-destructive hover:scale-105"
                      >
                        <Trash2 size={14} />
                      </button>
                    )}
                    {/* Collected badge */}
                    {collected > 0 && (
                      <div className="absolute bottom-2.5 start-2.5 flex items-center gap-1 bg-background/95 backdrop-blur-sm rounded-full px-2.5 py-1 shadow-sm">
                        <CircleDollarSign size={12} className="text-primary" />
                        <span className="text-[11px] font-semibold text-foreground font-sans">{currency} {collected.toFixed(0)}</span>
                      </div>
                    )}
                  </div>
                  <div className="px-3.5 py-3 space-y-1">
                    <h3 className="text-sm font-semibold text-foreground truncate leading-tight">
                      {c.title || c.recipient_name || t("dashboard.untitledDraft", { defaultValue: "Untitled draft" })}
                    </h3>
                    {c.type && (
                      <span className="block text-[10px] text-muted-foreground font-sans truncate">
                        {t(`dashboard.type.${c.type}`, {
                          defaultValue: c.type === "card_collection" ? "Card + Collection" : "Card",
                        })}
                      </span>
                    )}
                    {c.pool_type && (
                      <span className="block text-[10px] text-muted-foreground font-sans truncate">
                        {t(`dashboard.poolType.${c.pool_type}`, {
                          defaultValue:
                            c.pool_type === "product"
                              ? "Product pool"
                              : c.pool_type === "sign_only"
                                ? "Sign only"
                                : "Money pool",
                        })}
                      </span>
                    )}
                    {isDraft && (
                      <span className="inline-flex items-center rounded-md border border-primary/30 bg-primary/10 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider text-primary">
                        {t("dashboard.status.draft", { defaultValue: "Draft" })}
                      </span>
                    )}
                    {!isDraft && (
                      <div className="flex items-center justify-between">
                        <span className="text-[10px] text-muted-foreground font-sans">
                          {isParticipating
                            ? `${signerCount} ${signerCount === 1 ? t("dashboard.signer") : t("dashboard.signers")}`
                            : t(`dashboard.status.${c.status}`)}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </main>

      {/* Confirm dialog for deleting a draft. Rendered at the page
          level (not inside the tile map) so the AlertDialog portal
          mounts once per page render and the transition state lives
          outside the mapped tiles. */}
      <AlertDialog
        open={!!draftToDelete}
        onOpenChange={(open) => {
          if (!open && !deletingDraft) setDraftToDelete(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t("dashboard.deleteDraftTitle", { defaultValue: "Delete this draft?" })}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t("dashboard.deleteDraftDesc", {
                defaultValue:
                  "The draft card will be permanently removed. This can't be undone.",
              })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deletingDraft}>
              {t("common.cancel", { defaultValue: "Cancel" })}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                confirmDeleteDraft();
              }}
              disabled={deletingDraft}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90 gap-1.5"
            >
              {deletingDraft && <Loader2 size={14} className="animate-spin" />}
              {t("common.delete", { defaultValue: "Delete" })}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Dashboard;
